import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Lock, Ban, BookX, Loader2, Hourglass, Briefcase } from 'lucide-react';
import LoadingScreen from './LoadingScreen';

const StudentRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState('loading'); 
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    const checkAccess = async () => {
      const { data: { user } } = await supabase.auth.getUser();

      if (!mounted) return;

      // 1. UTENTE NON LOGGATO
      if (!user) {
        setAccessStatus('not-logged');
        setLoading(false);
        return;
      }

      const email = user.email || '';
      
      // 2. ADMIN (Accesso Totale)
      if (email === 'info@tusciahub.it') {
         setAccessStatus('allowed');
         setLoading(false);
         return;
      }

      // 3. STUDENTI UNITUS (Accesso Totale)
      const isUnitus = email.endsWith('@studenti.unitus.it') || email.endsWith('@unitus.it');
      if (isUnitus) {
        setAccessStatus('allowed');
        setLoading(false);
        return;
      }

      // 4. CONTROLLO RUOLI DATABASE
      const { data: profile } = await supabase
        .from('profiles')
        .select('role, is_approved')
        .eq('id', user.id)
        .single();

      if (profile) {
        // --- LOGICA AZIENDA ---
        if (profile.role === 'azienda') {
            
            // Definisco le pagine permesse alle aziende
            const allowedCompanyPaths = ['/partner', '/jobs', '/eventi', '/merch', '/profile'];
            const isAllowedPath = allowedCompanyPaths.some(path => location.pathname.startsWith(path));

            // A) PRIMO CONTROLLO: SEI NELLA SEZIONE GIUSTA?
            // Se l'azienda prova ad andare su Dipartimenti/Note/Ecc -> BLOCCO SUBITO (Indipendentemente dall'approvazione)
            if (!isAllowedPath) {
                setAccessStatus('restricted-company');
                setLoading(false);
                return;
            }

            // B) SECONDO CONTROLLO: SEI NELLA SEZIONE GIUSTA (es. Jobs), MA SEI APPROVATO?
            if (!profile.is_approved) {
                if (location.pathname.startsWith('/profile')) {
                    setAccessStatus('allowed');
                } else {
                    setAccessStatus('waiting-approval');
                }
                setLoading(false);
                return;
            }

            // C) SEI NELLA SEZIONE GIUSTA E SEI APPROVATO
            setAccessStatus('allowed');

        } 
        // --- LOGICA PRIVATO (né studente né azienda) ---
        else {
            // Solo Mercatino, Jobs, Ripetizioni, Affitti, Merch, Profile
            const allowedPrivatePaths = ['/mercatino', '/jobs', '/ripetizioni', '/affitti', '/merch', '/profile'];
            const isAllowed = allowedPrivatePaths.some(path => location.pathname.startsWith(path));

            if (isAllowed) {
                setAccessStatus('allowed');
            } else {
                setAccessStatus('restricted-private');
            }
        }
      } else {
        setAccessStatus('not-unitus');
      }
      
      setLoading(false);
    };

    checkAccess();
    return () => { mounted = false; };
  }, [location.pathname]);

  const handleLogoutAndRedirect = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  if (loading) return <LoadingScreen />;

  if (accessStatus === 'allowed') return children;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center">
      <div className="bg-white p-10 rounded-[2.5rem] shadow-xl max-w-md border border-slate-100 relative overflow-hidden animate-in zoom-in-95 duration-300">
        
        {/* CASO 1: AZIENDA SU PAGINA GIUSTA MA NON APPROVATA (In Attesa) */}
        {accessStatus === 'waiting-approval' && (
            <>
                <div className="mx-auto bg-amber-50 w-24 h-24 rounded-full flex items-center justify-center mb-6 border border-amber-100 shadow-sm">
                    <Hourglass className="h-10 w-10 text-amber-500 animate-pulse" />
                </div>
                <h2 className="text-2xl font-black text-slate-900 mb-3">In Attesa di Approvazione</h2>
                <div className="text-slate-500 mb-8 space-y-3 font-medium">
                    <p className="text-amber-600 font-bold">Il tuo account aziendale è in fase di verifica.</p>
                    <p className="text-sm">Potrai accedere a questa sezione (Partner/Lavoro/Eventi) non appena un amministratore approverà la tua richiesta.</p>
                </div>
            </>
        )}

        {/* CASO 2: AZIENDA (Approvata o No) SU PAGINA VIETATA (Es. Dipartimenti) */}
        {accessStatus === 'restricted-company' && (
            <>
                <div className="mx-auto bg-indigo-50 w-24 h-24 rounded-full flex items-center justify-center mb-6 border border-indigo-100">
                    <Briefcase className="h-10 w-10 text-indigo-600" />
                </div>
                <h2 className="text-2xl font-black text-slate-900 mb-3">Area Didattica</h2>
                <div className="text-slate-500 mb-8 space-y-4 font-medium">
                    <p className="text-sm">
                        Questa sezione (Dispense, Mercatino o Affitti) è riservata esclusivamente agli <b>studenti universitari</b>.
                    </p>
                    <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 text-xs text-left">
                        <p className="font-bold text-slate-700 mb-2">Come Azienda puoi accedere solo a:</p>
                        <ul className="list-disc list-inside space-y-1 text-slate-500">
                            <li>Partner & Sconti</li>
                            <li>Offerte di Lavoro</li>
                            <li>Eventi & Nightlife</li>
                            <li>Merchandising</li>
                        </ul>
                    </div>
                </div>
            </>
        )}

        {/* CASO 3: PRIVATO SU PAGINA VIETATA */}
        {accessStatus === 'restricted-private' && (
            <>
                <div className="mx-auto bg-red-50 w-24 h-24 rounded-full flex items-center justify-center mb-6 border border-red-100">
                    <Ban className="h-10 w-10 text-red-500" />
                </div>
                <h2 className="text-2xl font-black text-slate-900 mb-3">Solo Universitari</h2>
                <div className="text-slate-500 mb-8 space-y-2 font-medium">
                    <p className="text-red-500 font-bold">Questa sezione è riservata.</p>
                    <p className="text-sm">
                        Il materiale didattico (Dispense ed Esami) è accessibile solo agli studenti con email istituzionale <b>@studenti.unitus.it</b>.
                    </p>
                </div>
            </>
        )}

        {/* CASO 4: NON LOGGATO */}
        {accessStatus === 'not-logged' && (
            <>
                 <div className="mx-auto bg-slate-100 w-24 h-24 rounded-full flex items-center justify-center mb-6 border border-slate-200">
                    <Lock className="h-10 w-10 text-slate-500" />
                </div>
                <h2 className="text-2xl font-black text-slate-900 mb-3">Effettua l'accesso</h2>
                <p className="text-slate-500 mb-8 font-medium">Devi essere loggato per vedere questa pagina.</p>
            </>
        )}

        {/* CASO 5: GENERICO */}
        {accessStatus === 'not-unitus' && (
            <>
                 <div className="mx-auto bg-slate-100 w-24 h-24 rounded-full flex items-center justify-center mb-6 border border-slate-200">
                    <Ban className="h-10 w-10 text-slate-500" />
                </div>
                <h2 className="text-2xl font-black text-slate-900 mb-3">Non Autorizzato</h2>
                <p className="text-slate-500 mb-8 font-medium">Non hai i permessi per visualizzare questa pagina.</p>
            </>
        )}

        <div className="space-y-3">
            {accessStatus !== 'not-logged' && (
                <button onClick={handleLogoutAndRedirect} className="block w-full bg-slate-900 text-white px-6 py-3.5 rounded-2xl font-bold hover:bg-slate-800 transition shadow-lg hover:-translate-y-1">
                  Logout
                </button>
            )}
            {accessStatus === 'not-logged' && (
                <button onClick={() => navigate('/login')} className="block w-full bg-indigo-600 text-white px-6 py-3.5 rounded-2xl font-bold hover:bg-indigo-700 transition shadow-lg hover:-translate-y-1">
                  Vai al Login
                </button>
            )}
            <Link to="/" className="block w-full text-slate-400 hover:text-indigo-600 text-sm font-bold pt-2 transition">Torna alla Home</Link>
        </div>
      </div>
    </div>
  );
};

export default StudentRoute;